import { useEffect, useState } from "react";
import { Store, ArrowRight, Loader2 } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const SLUG_KEY = "pos_store_slug";
const NAME_KEY = "pos_store_name";

export const rememberStore = (store) => {
  try { localStorage.setItem(SLUG_KEY, store.slug); localStorage.setItem(NAME_KEY, store.name || ""); } catch { /* */ }
};
export const forgetStore = () => {
  try { localStorage.removeItem(SLUG_KEY); localStorage.removeItem(NAME_KEY); } catch { /* */ }
};

// Store slug for this device: ?store= → remembered → last logged-in tenant (existing devices keep working)
export const initialStoreSlug = () => {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get("store");
    if (fromUrl) return fromUrl.trim().toLowerCase();
    const saved = localStorage.getItem(SLUG_KEY);
    if (saved) return saved;
    const active = localStorage.getItem("_active_tenant_slug");
    if (active) return active;
    const cached = JSON.parse(localStorage.getItem("auth_cache") || "null");
    return cached?.tenant?.slug || "";
  } catch { return ""; }
};

export const resolveStore = (q) => axios.get(`${API}/api/auth/store/resolve`, { params: { q }, timeout: 12000 }).then((r) => r.data.store);

const errText = (e, fallback) => {
  const d = e?.response?.data?.detail;
  return typeof d === "string" ? d : fallback;
};

export function StoreGate({ t, onResolved }) {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { setError(""); }, [q]);

  const submit = async (e) => {
    e?.preventDefault();
    if (q.trim().length < 3 || busy) return;
    setBusy(true);
    try {
      const store = await resolveStore(q.trim());
      rememberStore(store);
      onResolved(store);
    } catch (err) {
      setError(errText(err, t("login_store_not_found")));
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="store-gate">
      <div className="flex items-start gap-3 p-3.5 rounded-2xl bg-th-card border border-th-border">
        <div className="w-11 h-11 rounded-xl bg-th-surface flex items-center justify-center flex-shrink-0">
          <Store className="w-5 h-5 text-th-dim" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-cream-900 font-semibold text-sm">{t("login_store_title")}</p>
          <p className="text-th-dim text-xs mt-0.5">{t("login_store_hint")}</p>
        </div>
      </div>
      <input
        value={q} onChange={(e) => setQ(e.target.value)} autoFocus autoComplete="off" spellCheck={false}
        placeholder={t("login_store_placeholder")} data-testid="store-id-input"
        className="w-full h-12 px-4 rounded-2xl bg-th-card border border-th-border text-cream-900 text-sm placeholder:text-th-dim focus:outline-none focus:border-th-accent"
      />
      {error && <p className="text-sm text-red-600 px-1" data-testid="store-gate-error">{error}</p>}
      <button type="submit" disabled={q.trim().length < 3 || busy} data-testid="store-continue-btn"
        className="w-full h-12 rounded-2xl bg-cream-900 text-white font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-50 hover:opacity-90 transition-opacity">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{t("login_store_continue")} <ArrowRight className="w-4 h-4" /></>}
      </button>
    </form>
  );
}

export function StoreBadge({ store, t, onSwitch }) {
  return (
    <div className="flex items-center gap-3 p-3 rounded-2xl bg-th-surface border border-th-border" data-testid="login-store-badge">
      <div className="w-9 h-9 rounded-xl bg-white flex items-center justify-center flex-shrink-0">
        <Store className="w-4 h-4 text-cream-900" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-cream-900 font-semibold text-sm truncate" data-testid="login-store-name">{store.name}</p>
        <p className="text-th-dim text-[11px] truncate font-mono" data-testid="login-store-slug">{store.slug}</p>
      </div>
      <button type="button" onClick={onSwitch} data-testid="switch-store-btn"
        className="text-xs font-semibold text-th-dim hover:text-cream-900 underline-offset-2 hover:underline">
        {t("login_switch_store")}
      </button>
    </div>
  );
}
